import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, User } from 'lucide-react';
import gsap from 'gsap';
import { ChatLogic } from '../logic/chatLogic';
import { ASSETS } from '../assetManifest';

function MessageBubble({ message, characterImage, chatDisplayName }) {
  return (
    <div className={`flex flex-col mb-4 ${message.sender === 'player' ? 'items-end' : 'items-start'}`}>
      <div className={`flex items-end ${message.sender === 'player' ? 'justify-end' : 'justify-start'}`}>
        {message.sender !== 'player' && (
          <div className="character-icon-container mr-2" style={{ minWidth: '40px', width: '40px', height: '40px' }}>
            <img src={characterImage} alt="Character" className="character-icon" />
          </div>
        )}
        <div className="flex flex-col">
          <div className={`text-sm text-gray-600 mb-1 ${message.sender === 'player' ? 'text-right' : 'text-left'}`}>
            {message.sender === 'player' ? 'You' : chatDisplayName}
          </div>
          <div className={`inline-block p-3 rounded-lg ${message.sender === 'player' ? 'bg-blue-500 text-white' : 'bg-gray-300'}`}>
            {message.text}
          </div>
        </div>
        {message.sender === 'player' && (
          <div className="ml-2">
            <User size={30} />
          </div>
        )}
      </div>
    </div>
  );
}

function Chat({ app, chatId }) {
  const [messages, setMessages] = useState([]);
  const [choices, setChoices] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [showGlowingBadge, setShowGlowingBadge] = useState(false);
  const chatContainerRef = useRef(null);
  const chatLogic = useRef(new ChatLogic(chatId)).current;

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    const fetchNext = async () => {
      const next = await chatLogic.getNext();
      if (next === null) {
        setShowGlowingBadge(true);
        app.showReward(chatLogic.chatData.completion_reward);
      } else if (typeof next === 'object' && next.choices) {
        setChoices(next.choices);
      } else {
        if (next.sender === 'player' || messages.length === 0) {
          setMessages(prev => [...prev, next]);
        } else {
          setIsTyping(true);
          setTimeout(() => {
            setMessages(prev => [...prev, next]);
            setIsTyping(false);
          }, 1000);
        }
      }
    };

    if (!choices && !isTyping) {
      fetchNext();
    }
  }, [chatId, messages, choices, isTyping]);

  const handleChoice = async (choice) => {
    setChoices(null);
    await chatLogic.makeChoice(app, choice);
  };

  const characterPortrait = ASSETS.portraits[chatLogic.chatData.characterId].path;

  return (
    <div className="bg-gray-100 h-full w-full flex flex-col">
      <style>
        {`
          @keyframes typingAnimation {
            0% { opacity: 0.2; }
            50% { opacity: 1; }
            100% { opacity: 0.2; }
          }
          .typing-indicator {
            display: inline-flex;
            align-items: center;
            padding: 6px 12px;
            background-color: #e5e5ea;
            border-radius: 18px;
          }
          .typing-indicator span {
            height: 8px;
            width: 8px;
            margin: 0 2px;
            background-color: #3b3b3b;
            border-radius: 50%;
            display: inline-block;
            animation: typingAnimation 1s infinite;
          }
          .typing-indicator span:nth-child(2) {
            animation-delay: 0.2s;
          }
          .typing-indicator span:nth-child(3) {
            animation-delay: 0.4s;
          }
          .character-icon-container {
            width: 40px;
            height: 40px;
            overflow: hidden;
            border-radius: 50%;
          }
          .character-icon {
            width: 100%;
            height: auto;
            object-fit: cover;
            object-position: top;
            transform: scale(4);
            transform-origin: top;
          }
        `}
      </style>
      <div className="bg-gray-200 p-4 flex items-center">
        <button onClick={() => app.goToHome()} className="mr-4 relative">
          <ArrowLeft size={24} />
          {showGlowingBadge && (
            <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse"></span>
          )}
        </button>
        <div className="character-icon-container mr-4">
          <img src={characterPortrait} alt={chatLogic.chatData.chatDisplayName} className="character-icon" />
        </div>
        <h2 className="text-xl font-bold">{chatLogic.chatData.chatDisplayName}</h2>
      </div>
      <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4">
        {messages.map((message, index) => (
          <MessageBubble 
            key={index} 
            message={message} 
            characterImage={characterPortrait}
            chatDisplayName={chatLogic.chatData.chatDisplayName}
          />
        ))}
        {isTyping && (
          <div className="mb-4 flex items-end">
            <div className="character-icon-container mr-2" style={{ minWidth: '40px', width: '40px', height: '40px' }}>
              <img src={characterPortrait} alt={chatLogic.chatData.chatDisplayName} className="character-icon" />
            </div>
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}
      </div>
      {choices && choices.length > 0 && (
        <div className="p-4 bg-white">
          {choices.map((choice, index) => (
            <button
              key={index}
              className="w-full mb-2 p-3 bg-gray-200 rounded-lg text-left"
              onClick={() => handleChoice(choice)}
            >
              {choice.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default Chat;
